import { Link } from "react-router-dom";
import MarketingNav from "@/components/brand/MarketingNav";
import MarketingFooter from "@/components/brand/MarketingFooter";
import { useState } from "react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";

const FEATURES = [
  { title: "Keyword intelligence", body: "Discover the long-tail clusters that map to real revenue — from top-of-funnel to buying intent.", num: "01" },
  { title: "AI article generator", body: "Brief → outline → publish-ready draft, tuned for the primary conversion event of your business model.", num: "02" },
  { title: "Growth plan engine", body: "30/60/90-day plan covering pages, content, comparison assets, and AI-search optimization.", num: "03" },
  { title: "AI search visibility", body: "Track how often your brand can be recommended by AI assistants — and what content closes the gap.", num: "04" },
  { title: "Revenue growth dashboard", body: "Revenue and conversions by content source, adapted to your industry — orders, MRR, leads, or bookings.", num: "05" },
  { title: "Authority workflows", body: "Ethical digital PR queues for publications, communities, newsletters, and podcasts in your vertical.", num: "06" },
];

const HOW = [
  { step: "Connect", body: "Point TrendRise AI at your website, store, or landing page. We surface every keyword, page, and conversion gap." },
  { step: "Plan", body: "Generate a 30/60/90-day plan tailored to your business model, audience, and primary conversion event." },
  { step: "Publish", body: "AI briefs, outlines, drafts, and metadata — all tuned for SEO and your funnel." },
  { step: "Compound", body: "Track AI-search visibility, internal linking depth, and revenue lift by source." },
];

const INDUSTRIES = [
  "Ecommerce", "Shopify", "WooCommerce", "Dropshipping", "Physical products",
  "Digital products", "Ebooks & publishing", "SaaS", "Subscription", "Courses",
  "Memberships", "Creators", "Agencies", "Freelancers", "Consultants",
  "Professional services", "Local business", "Marketplaces", "Startups",
];

const FAQ = [
  { q: "Which businesses is TrendRise AI built for?", a: "Any business selling online — ecommerce, Shopify, dropshipping, SaaS, digital products, courses, memberships, creators, agencies, services, local businesses, marketplaces, and ebook publishers. The platform detects your business model and adapts its terminology and recommendations accordingly." },
  { q: "Will this guarantee #1 rankings?", a: "No. We don't make ranking guarantees. We surface the highest-leverage keyword, content, and authority opportunities and help you execute against them with measurable, compounding output." },
  { q: "How does AI search visibility work?", a: "We simulate buying-intent prompts across AI assistants and score current positioning. We then recommend exact pages, schema, and entities to publish so the brand can be cited in AI answers." },
  { q: "Do you publish content automatically?", a: "By default, every article goes through a human review queue. You can switch to auto-publish per project once you're confident in voice and conversion blocks." },
  { q: "Can I use my own LLM key?", a: "Yes. The platform ships with a universal LLM key for fast onboarding and supports custom OpenAI, Anthropic, and Gemini keys at any time." },
  { q: "What plans do you offer?", a: "Starter at $49/mo, Growth at $149/mo, Scale at $399/mo, and Enterprise at $999/mo. No surprise overage bills — when you reach your included usage, upgrade or add prepaid credits. All plans include the AI article engine, keyword intelligence, and AI-search visibility. See the pricing page for included limits." },
];

export default function LandingPage() {
  const [hero] = useState("https://images.unsplash.com/photo-1739918075668-fc7844c6d921?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxkYXJrJTIwZWxlZ2FudCUyMGxpYnJhcnklMjBpbnRlcmlvcnxlbnwwfHx8fDE3Nzc4NDAyMDJ8MA&ixlib=rb-4.1.0&q=85");

  return (
    <div className="dark-marketing grain min-h-screen relative overflow-x-hidden" data-testid="landing-page">
      <MarketingNav />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6 sm:px-12 lg:px-24 relative">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7 scroll-fade">
            <p className="editorial-overline" data-testid="hero-eyebrow">AI-POWERED GROWTH AUTOMATION</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-ivory-50 mt-6">
              Turn every product into <em className="text-gold-500 not-italic font-normal">compounding</em> growth.
            </h1>
            <p className="mt-6 text-lg text-ivory-200/80 max-w-2xl leading-relaxed" data-testid="hero-subheadline">
              TrendRise AI discovers growth opportunities, creates high-performing content, improves search and AI visibility, monitors performance, and helps turn traffic into revenue — automatically.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/signup" data-testid="hero-primary-cta" className="px-7 py-3.5 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-sm text-sm transition-colors text-center">
                Start Growth Autopilot
              </Link>
              <Link to="/login" data-testid="hero-secondary-cta" className="px-7 py-3.5 border border-ivory-200/30 hover:border-gold-500 text-ivory-50 hover:text-gold-500 rounded-sm text-sm font-semibold transition-colors text-center">
                View Demo Dashboard
              </Link>
            </div>
            <p className="mt-10 text-sm text-ivory-200/70 max-w-xl leading-relaxed" data-testid="hero-industries">
              Built for ecommerce, Shopify, dropshipping, SaaS, digital products, creators, agencies, services, courses, subscriptions, and more.
            </p>
          </div>

          {/* Product UI mockup */}
          <div className="lg:col-span-5">
            <div className="glass rounded-sm p-5">
              <div className="flex items-center justify-between mb-4">
                <span className="editorial-overline">Aurora — Shopify · Live</span>
                <span className="text-[10px] text-emerald-300 uppercase tracking-widest">▲ +24% MoM</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { l: "Organic traffic", v: "58,025" },
                  { l: "Orders", v: "482" },
                  { l: "Keywords tracked", v: "1,248" },
                  { l: "AI visibility", v: "41.2 / 100" },
                ].map((k) => (
                  <div key={k.l} className="border border-ivory-200/10 p-3 rounded-sm">
                    <p className="text-[10px] uppercase tracking-widest text-ivory-200/60">{k.l}</p>
                    <p className="font-serif text-2xl text-ivory-50 mt-1">{k.v}</p>
                  </div>
                ))}
              </div>
              <div className="mt-4 border border-ivory-200/10 rounded-sm p-3">
                <p className="text-[10px] uppercase tracking-widest text-ivory-200/60 mb-2">30-day traffic</p>
                <svg viewBox="0 0 200 60" className="w-full h-16">
                  <polyline
                    fill="none"
                    stroke="#C5A059"
                    strokeWidth="1.4"
                    points="0,50 12,46 24,42 36,40 48,38 60,33 72,32 84,28 96,29 108,24 120,22 132,18 144,20 156,14 168,12 180,9 192,7 200,5"
                  />
                  <polyline
                    fill="rgba(197,160,89,0.08)"
                    stroke="none"
                    points="0,60 0,50 12,46 24,42 36,40 48,38 60,33 72,32 84,28 96,29 108,24 120,22 132,18 144,20 156,14 168,12 180,9 192,7 200,5 200,60"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <img src={hero} alt="" className="absolute inset-0 -z-10 w-full h-full object-cover opacity-[0.07] pointer-events-none" />
      </section>

      <div className="gold-divider w-2/3 mx-auto opacity-50" />

      {/* Problem */}
      <section className="px-6 sm:px-12 lg:px-24 py-24" id="problem">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="editorial-overline">The problem</p>
            <h2 className="font-serif text-4xl text-ivory-50 mt-4 leading-tight">Most sites are invisible to the customers already looking.</h2>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
            {[
              { t: "Thin category and product pages", b: "Search engines and AI assistants can't tell why your product matters against the alternatives." },
              { t: "Low repeat conversion", b: "Without a clear reading path from content to conversion, curiosity never compounds into revenue." },
              { t: "Invisible to AI search", b: "ChatGPT and Perplexity already answer 'what's the best {your category}?' — but your brand isn't in the answer." },
              { t: "No content engine", b: "One post a month won't move the needle. You need a compounding pipeline aligned to revenue." },
            ].map((x) => (
              <div key={x.t} className="border-l border-gold-500/30 pl-5">
                <h3 className="font-serif text-xl text-ivory-50">{x.t}</h3>
                <p className="text-sm text-ivory-200/70 mt-2 leading-relaxed">{x.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution / Features */}
      <section id="features" className="px-6 sm:px-12 lg:px-24 py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl">
            <p className="editorial-overline">Solution</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-ivory-50 mt-4 leading-tight">An AI growth engine that adapts to your business model.</h2>
            <p className="mt-5 text-ivory-200/70 leading-relaxed">Ecommerce, SaaS, agencies, creators, or local businesses — TrendRise AI adapts terminology, KPIs, and recommendations to how your business actually converts.</p>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f) => (
              <div key={f.title} className="glass rounded-sm p-7 hover:border-gold-500/40 transition-colors" data-testid={`feature-${f.num}`}>
                <p className="font-serif text-gold-500 text-sm">{f.num}</p>
                <h3 className="font-serif text-2xl text-ivory-50 mt-3">{f.title}</h3>
                <p className="text-sm text-ivory-200/70 mt-3 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="px-6 sm:px-12 lg:px-24 py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <p className="editorial-overline text-center">Built for</p>
          <h2 className="font-serif text-4xl text-ivory-50 mt-4 text-center leading-tight">Any business selling online.</h2>
          <div className="mt-10 flex flex-wrap justify-center gap-2.5" data-testid="industries-grid">
            {INDUSTRIES.map((n) => (
              <span key={n} className="px-4 py-2 border border-ivory-200/15 text-ivory-200/80 rounded-sm text-sm hover:border-gold-500/50 hover:text-gold-500 transition-colors">
                {n}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 sm:px-12 lg:px-24 py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="editorial-overline">How it works</p>
            <h2 className="font-serif text-4xl text-ivory-50 mt-4 leading-tight">A four-step loop that compounds every week.</h2>
          </div>
          <div className="lg:col-span-8">
            <ol className="space-y-8">
              {HOW.map((h, i) => (
                <li key={h.step} className="flex gap-6 border-b border-ivory-200/10 pb-7 last:border-0">
                  <span className="font-serif text-gold-500 text-3xl shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <h3 className="font-serif text-xl text-ivory-50">{h.step}</h3>
                    <p className="text-sm text-ivory-200/70 mt-2 leading-relaxed">{h.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* AI search visibility */}
      <section className="px-6 sm:px-12 lg:px-24 py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="editorial-overline">AI search visibility</p>
            <h2 className="font-serif text-4xl text-ivory-50 mt-4 leading-tight">Be the answer when AI assistants get asked.</h2>
            <p className="mt-5 text-ivory-200/70 leading-relaxed text-sm">We simulate the prompts your future customers ask AI assistants — then recommend exact pages, schema, and entity coverage to make your brand the cited answer.</p>
          </div>
          <div className="glass rounded-sm p-6 space-y-4">
            {[
              { p: "What's the best Calendly alternative for a 100-person sales team?", s: 39 },
              { p: "Best Shopify skincare brand for sensitive skin?", s: 44 },
              { p: "Top online courses for product managers?", s: 41 },
            ].map((x) => (
              <div key={x.p} className="border border-ivory-200/10 p-4 rounded-sm">
                <p className="text-sm text-ivory-50 font-serif">&ldquo;{x.p}&rdquo;</p>
                <div className="flex items-center gap-3 mt-3">
                  <div className="flex-1 h-1 bg-ivory-200/10 rounded-sm overflow-hidden">
                    <div className="h-full bg-gold-500" style={{ width: `${x.s}%` }} />
                  </div>
                  <span className="text-xs text-gold-500 font-mono">{x.s} / 100</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="px-6 sm:px-12 lg:px-24 py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <p className="editorial-overline">Pricing</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-ivory-50 mt-4">From $49/mo to Enterprise at $999/mo.</h2>
          <p className="mt-4 text-ivory-200/70 max-w-2xl mx-auto text-sm">Every plan ships with the universal LLM key, adaptive dashboards, and the full growth plan engine. Upgrade as the revenue grows.</p>
          <Link to="/pricing" className="inline-block mt-8 px-7 py-3.5 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-sm text-sm transition-colors" data-testid="pricing-cta">
            See pricing
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 sm:px-12 lg:px-24 py-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto">
          <p className="editorial-overline text-center">Questions</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-ivory-50 mt-4 text-center">Frequently asked.</h2>
          <Accordion type="single" collapsible className="mt-10 space-y-2">
            {FAQ.map((f) => (
              <AccordionItem key={f.q} value={f.q} className="border border-ivory-200/10 rounded-sm px-5 bg-white/[0.02]">
                <AccordionTrigger className="text-ivory-50 font-serif text-lg hover:no-underline" data-testid={`faq-trigger-${f.q.slice(0, 20)}`}>
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-ivory-200/70 leading-relaxed text-sm">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 sm:px-12 lg:px-24 py-32 border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl sm:text-6xl text-ivory-50 leading-[1.05]">
            Turn trends into <em className="text-gold-500 not-italic">compounding growth</em>.
          </h2>
          <p className="mt-6 text-ivory-200/70 max-w-2xl mx-auto">Start the growth autopilot today. Ship better content. Keep your customers converting.</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/signup" className="px-8 py-3.5 bg-gold-500 hover:bg-gold-400 text-navy-900 font-semibold rounded-sm text-sm transition-colors" data-testid="final-cta-primary">
              Start Growth Autopilot
            </Link>
            <Link to="/login" className="px-8 py-3.5 border border-ivory-200/20 hover:border-gold-500 text-ivory-50 hover:text-gold-500 rounded-sm text-sm font-semibold transition-colors" data-testid="final-cta-secondary">
              View Demo Dashboard
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
